import { useMontageService } from "app/hooks/shared/standard-product/useMontageService";
import { useStandardProductAvailability } from "app/hooks/shared/standard-product/useStandardProductAvailability";
import { useStandardProductDeliveryCostsCalculator } from "app/hooks/shared/standard-product/useStandardProductDeliveryCostsCalculator";
import {
  StandardProductReceiveMethod,
  useStandardProductReceiveMethod,
} from "app/hooks/shared/standard-product/useStandardProductReceiveMethod";
import { useStandardProductType } from "app/hooks/shared/standard-product/useStandardProductType";
import { useCartContext } from "app/providers/CartProvider";
import CheckoutStandardProductReceiveMethod from "entities/checkout/standard-product-receive-method";
import { useMemo } from "react";
import { concatClassnames } from "shared/utils/concatClassnames";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { MONTAGE_SERVICE_TYPE_CODE } from "shared/utils/standard-product";

interface CheckoutStandardProductReceiveMethodProps {
  className?: string;
}

const StandardProductReceiveMethods = ({
  className,
}: CheckoutStandardProductReceiveMethodProps) => {
  const { getCart, updateGeneralCartValue, updateCart } = useCartContext();
  const cart = getCart();
  const productAnalysis = useStandardProductType(cart);
  const { freeGlueProduct } = useMontageService();
  const { getStandardProductsAviability, isReceiveMethodAvailable } =
    useStandardProductAvailability();
  const {
    standardProductDeliveryCosts,
    standardProductAssemblyCosts,
    getPanelTotalProductsCount,
  } = useStandardProductDeliveryCostsCalculator(cart, true);
  const panelAvailability = useMemo(
    () => getStandardProductsAviability(cart.standardProducts),
    [cart.standardProducts],
  );
  const currentStandardProductReceiveMethod =
    useStandardProductReceiveMethod(cart);

  const onStandardProductReceiveMethodChange = (
    method: StandardProductReceiveMethod,
  ) => {
    let standardProducts = [...cart.standardProducts];
    if (
      currentStandardProductReceiveMethod ===
        StandardProductReceiveMethod.DELIVERYWITHASSEMBLY &&
      method !== StandardProductReceiveMethod.DELIVERYWITHASSEMBLY
    ) {
      standardProducts = standardProducts.filter(
        (product) =>
          product.productType.code !== MONTAGE_SERVICE_TYPE_CODE &&
          product.productType.code !== "montageachtergrond",
      );
    }

    if (method === StandardProductReceiveMethod.DELIVERY) {
      updateGeneralCartValue({
        pickupWarehouse: "",
        delivery: {
          type: "gerse-bezorgdienst",
          date: "",
        },
        bumbalFlow: "shipment",
        withAssembly: false,
        standardProducts: standardProducts,
      });
    }
    if (method === StandardProductReceiveMethod.DELIVERYWITHASSEMBLY) {
      updateGeneralCartValue({
        pickupWarehouse: "",
        delivery: {
          type: "gerse-bezorgdienst",
          date: "",
        },
        bumbalFlow: "placement",
        withAssembly: true,

        //Add free glue product
        standardProducts: [
          ...cart.standardProducts,
          { ...freeGlueProduct, amount: getPanelTotalProductsCount },
        ],
      });
    }

    if (method === StandardProductReceiveMethod.PICK_UP_ESD) {
      updateGeneralCartValue({
        pickupWarehouse: "ESD",
        delivery: {
          type: "pick-up",
          date: "",
        },
        bumbalFlow: "",
        withAssembly: false,
        standardProducts: standardProducts,
      });
    }
    if (method === StandardProductReceiveMethod.PICK_UP_VILLAARENA) {
      updateGeneralCartValue({
        pickupWarehouse: "VillaArena",
        delivery: {
          type: "pick-up",
          date: "",
        },
        bumbalFlow: "",
        withAssembly: false,
        standardProducts: standardProducts,
      });
    }
  };

  const deliveryText = (
    <span>
      {" "}
      Je bestelling wordt geleverd aan het door jou opgegeven adres. Als je voor
      deze optie kiest, wordt er een bedrag van{" "}
      <span className="gg-font-bold">
        {getFormattedPrice(standardProductDeliveryCosts)}
      </span>
      bezorgkosten in rekening gebracht.
    </span>
  );

  const withAssemblyText = (
    <span>
      Aan de hand van het aantal panelen in jouw winkelwagen hebben we berekend
      wat montageservice zal kosten. Als je voor deze optie kiest, betaal je{" "}
      <span className="gg-font-bold">
        {getFormattedPrice(standardProductAssemblyCosts)}
      </span>{" "}
      montagekosten.
    </span>
  );

  const amsterdamPickupText = (
    <span>
      {" "}
      Kom je bestelling <span className="gg-font-bold">gratis</span> afhalen.
      Hiervoor kun je zelf een gewenste datum opgeven.
    </span>
  );

  return (
    <div className={concatClassnames("gg-grid gg-gap-y-6", className)}>
      {/* {(panelAvailability.delivery || productAnalysis.someAccessoire) && ( */}

      <CheckoutStandardProductReceiveMethod
        id="panel-delivery-availability"
        isAvailable={isReceiveMethodAvailable(panelAvailability.delivery)}
        isSelected={
          currentStandardProductReceiveMethod ===
          StandardProductReceiveMethod.DELIVERY
        }
        headingText={"Ik wil mijn bestelling thuis laten bezorgen"}
        // text={panelAvailability.delivery.deliveryCondition.description}
        // text={
        // 	"Je bestelling wordt geleverd aan het door jou opgegeven adres. Als je voor deze optie kiest, wordt er een bedrag van €59,- bezorgkosten in rekening gebracht."
        // }
        text={deliveryText}
        onChange={() =>
          onStandardProductReceiveMethodChange(
            StandardProductReceiveMethod.DELIVERY,
          )
        }
      />
      {/*   )}*/}

      {/* {panelAvailability.delivery && productAnalysis.someAkoestischepanelen && ( */}
      {productAnalysis.someAkoestischepanelen && (
        <CheckoutStandardProductReceiveMethod
          id="panel-delivery-with-assembly-availability"
          isAvailable={isReceiveMethodAvailable(panelAvailability.delivery)}
          isSelected={
            currentStandardProductReceiveMethod ===
            StandardProductReceiveMethod.DELIVERYWITHASSEMBLY
          }
          headingText={
            "Ik wil mijn bestelling inclusief montageservice thuis laten bezorgen"
          }
          // text={panelAvailability.delivery.deliveryCondition.description}
          text={withAssemblyText}
          onChange={() =>
            onStandardProductReceiveMethodChange(
              StandardProductReceiveMethod.DELIVERYWITHASSEMBLY,
            )
          }
        />
      )}
      {/* )} */}

      {panelAvailability.pickUp.amsterdam &&
        (productAnalysis.someAkoestischepanelen ||
          productAnalysis.someAccessoireAkoestischPaneel) && (
          <CheckoutStandardProductReceiveMethod
            id="panel-amsterdam-availability"
            isAvailable={isReceiveMethodAvailable(
              panelAvailability.pickUp.amsterdam,
            )}
            isSelected={
              currentStandardProductReceiveMethod ===
              StandardProductReceiveMethod.PICK_UP_VILLAARENA
            }
            headingText={"Ik kom mijn bestelling ophalen in Amsterdam"}
            // text={panelAvailability.pickUp.amsterdam.deliveryCondition.description}
            text={amsterdamPickupText}
            onChange={() =>
              onStandardProductReceiveMethodChange(
                StandardProductReceiveMethod.PICK_UP_VILLAARENA,
              )
            }
          />
        )}

      {panelAvailability.pickUp.vlaardingen &&
        (productAnalysis.someAkoestischepanelen ||
          productAnalysis.someAccessoireAkoestischPaneel) && (
          <CheckoutStandardProductReceiveMethod
            id="panel-vlaardingen-availability"
            isAvailable={isReceiveMethodAvailable(
              panelAvailability.pickUp.vlaardingen,
            )}
            isSelected={
              currentStandardProductReceiveMethod ===
              StandardProductReceiveMethod.PICK_UP_ESD
            }
            headingText={"Ik kom mijn bestelling ophalen in Vlaardingen"}
            // text={
            // 	panelAvailability.pickUp.vlaardingen.deliveryCondition
            // 		.description
            // }
            text={amsterdamPickupText}
            onChange={() =>
              onStandardProductReceiveMethodChange(
                StandardProductReceiveMethod.PICK_UP_ESD,
              )
            }
          />
        )}
    </div>
  );
};
export default StandardProductReceiveMethods;
